/** @typedef {import('../types/compliance').KycDocument} KycDocument */
/** @typedef {import('../types/compliance').KycUpload} KycUpload */
/** @typedef {import('../types/api').ApiResponse} ApiResponse */
/** @typedef {import('../types/api').PaginatedResponse} PaginatedResponse */
/** @typedef {import('../types/api').PaginationParams} PaginationParams */

import { instance } from '../config/api';

/**
 * @param {Object} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const setKycInfo = async (body) => {
  const { data } = await instance.post(`kyc/info`, body);

  return data;
};

/**
 * @param {string} [kyc_type]
 * @returns {Promise<KycDocument[]>}
 */
const fetchKycDocuments = async (kyc_type = '') => {
  const { data } = await instance.get(`kyc/docs${kyc_type ? `?kyc_type=${kyc_type}` : ''}`);

  return data?.data;
};

/**
 * @param {Object} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const uploadKycDocument = async (body) => {
  const { data } = await instance.post(`kyc/uploads`, body);

  return data;
};

/**
 * @param {string} documentTitle
 * @returns {Promise<ApiResponse<Object>>}
 */
const skipKycDocument = async (documentTitle) => {
  const { data } = await instance.post(`kyc/skip-document`, {
    document_title: documentTitle
  });

  return data;
};

/**
 * @param {Object} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const submitKycDocument = async (body) => {
  const { data } = await instance.post(`kyc/submit-document`, body);

  return data;
};

/**
 * @param {string} reference
 * @param {Object} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const reuploadKycDocument = async (reference, body) => {
  const { data } = await instance.patch(`kyc/reupload-document/${reference}`, body);

  return data;
};

/** @returns {Promise<ApiResponse<Object>>} */
const getKycInfo = async () => {
  const { data } = await instance.get(`kyc/check`);
  return data;
};

// Admin KYC functions

/**
 * @param {PaginationParams & { kyc_type?: string }} params
 * @returns {Promise<PaginatedResponse<KycUpload>>}
 */
const fetchAllKycUploads = async (params) => {
  const { offset, searchQuery, status, kyc_type, dateFilter } = params;
  const { from, to } = dateFilter || {};
  const { data } = await instance.get(
    `kyc/uploads?limit=10${offset ? '&offset=' + offset : ''}${
      searchQuery ? '&search_term=' + searchQuery : ''
    }${status ? '&status=' + status : ''}${kyc_type ? '&kyc_type=' + kyc_type : ''}${
      from && to ? '&from=' + from + '&to=' + to : ''
    }`
  );
  return data;
};

/**
 * @param {string} reference
 * @returns {Promise<ApiResponse<KycUpload>>}
 */
const fetchKycUploadByReference = async (reference) => {
  const { data } = await instance.get(`kyc/uploads/${reference}`);
  return data;
};

/**
 * @param {number} merchantId
 * @returns {Promise<ApiResponse<KycUpload[]>>}
 */
const fetchKycUploadsForMerchant = async (merchantId) => {
  const { data } = await instance.get(`kyc/merchant/${merchantId}/uploads`);
  return data;
};

/**
 * @param {Object} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const reviewKycUpload = async (body) => {
  const { data } = await instance.patch(`kyc/review`, body);
  return data;
};

/**
 * @param {number} uploadId
 * @returns {Promise<ApiResponse<Object[]>>}
 */
const fetchKycComments = async (uploadId) => {
  const { data } = await instance.get(`kyc/comments/${uploadId}`);
  return data;
};

/**
 * @param {{ upload_id: number, comment: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const addKycComment = async (body) => {
  const { data } = await instance.post(`kyc/comments`, body);
  return data;
};

/**
 * @param {string} reference
 * @returns {Promise<string>} Signed URL
 */
const getDocumentSignedUrl = async (reference) => {
  const { data } = await instance.get(`kyc/uploads/${reference}/signed-url`);
  return data?.data?.signed_url;
};

/** @returns {Promise<ApiResponse<Object>>} */
const deleteKyc = async () => {
  const { data } = await instance.delete(`kyc/info`);
  return data;
};

export {
  addKycComment,
  deleteKyc,
  fetchAllKycUploads,
  fetchKycComments,
  fetchKycDocuments,
  fetchKycUploadByReference,
  fetchKycUploadsForMerchant,
  getDocumentSignedUrl,
  getKycInfo,
  reuploadKycDocument,
  reviewKycUpload,
  setKycInfo,
  skipKycDocument,
  submitKycDocument,
  uploadKycDocument
};
